import { bilingual, bilingualText, simplifiedLanguage } from "./bilingual.js";
import type { AssembledSquare, ShownAssembly } from "./fuxi-asking.js";
import { escapeHtml } from "../site-search/search-escaping.js";
import { typedField } from "../fuxi/answer-fields.js";

/**
 * The parts a 汉字 is put together from, and the sum the reader writes them in.
 *
 * Beside `./fuxi-tiles-markup.ts`, which draws the tiles a word-order card is
 * answered with. This card is answered the same way, a tile at a time. A part
 * goes into the next empty slot of a sum, `女 + 子`, and the character is put
 * together only once the card is checked. `src/site/fuxi-assembly.ts` moves
 * the parts and writes the order into the form.
 *
 * **No square on the question.** A square with a box per part shows where each
 * part sits, and a part drawn into its box looks right or wrong before the
 * card is checked. The reader would be reading the boxes and not recalling the
 * character. The sum shows how many parts there are and nothing else, and the
 * marked card is where they go together (`./fuxi-assembled-marked.ts`).
 *
 * **Each tile carries a digit, and the digit places it**, as on a word-order
 * card. Backspace and the Undo button take the last part back out, and a tap
 * on a part in the sum takes that one back. Filling the last slot checks the
 * card, which is the one press a reader would make next.
 *
 * **The form posts which tile went where**, as places in the row the card
 * showed. The handler shuffles the tiles again from the posted id and reads
 * the parts back. See `src/fuxi/hanzi-assembly-table.ts`.
 *
 * **With no script there is no card**, the same as handwriting.
 */

/** The 米字格 the marked square draws, the paper a character is learnt on. */
const grid =
  '<path class="cb-fuxi-zuzi-grid" d="M0 50H100M50 0V100M0 0L100 100M100 0L0 100"/>';

/** The plus between two terms of a sum. */
const plus = '<span class="cb-fuxi-zuzi-plus" aria-hidden="true">+</span>';

/**
 * Terms joined into a sum, `女 + 子`.
 *
 * @param terms - Each term, drawn already.
 * @returns The sum's inner markup.
 */
export function fuxiAssemblySum(terms: readonly string[]): string {
  return terms.join(plus);
}

/**
 * The square on a marked card, with its grid and a box per part.
 *
 * **Each box names the part it wants and the character's strokes that part
 * owns**, and the SVG names the character's outline file. That is what lets
 * `src/site/fuxi-assembled.ts` draw a part landing in its own box as those
 * strokes of the character. The boxes themselves are not drawn.
 *
 * @param properties - The square, and what is drawn in it.
 * @returns The SVG.
 */
export function fuxiAssemblyFrame(properties: {
  readonly square: AssembledSquare;
  readonly inside?: string;
}): string {
  const { boxes, parts, strokes, outlines } = properties.square;
  const drawn = boxes
    .map(
      ({ x, y, width, height }, at) =>
        `<rect class="cb-fuxi-zuzi-box" data-cb-fuxi-box="${String(at)}" data-cb-fuxi-box-part="${escapeHtml(parts[at] ?? "")}" data-cb-fuxi-box-strokes="${(strokes[at] ?? []).join(" ")}" x="${String(x)}" y="${String(y)}" width="${String(width)}" height="${String(height)}"/>`,
    )
    .join("\n");

  return `<svg class="cb-fuxi-zuzi-frame" viewBox="0 0 100 100" data-cb-fuxi-zuzi-frame data-cb-fuxi-zuzi-outlines="${escapeHtml(outlines)}">
${grid}
${drawn}${properties.inside ?? ""}
</svg>`;
}

/** One empty slot of the sum, which the next part fills. */
function slot(place: number): string {
  return `<span class="cb-fuxi-zuzi-slot" data-cb-fuxi-slot="${String(place)}"></span>`;
}

/** One tile, with its digit. */
function tile(part: string, place: number): string {
  return `<button class="cb-fuxi-tile cb-fuxi-zuzi-tile" type="button" data-cb-fuxi-part="${String(place)}"><span class="cb-fuxi-tile-key" aria-hidden="true">${String(place + 1)}</span><span lang="${simplifiedLanguage}">${escapeHtml(part)}</span></button>`;
}

/**
 * The sum, the tiles, Undo, and the field the order is posted in.
 *
 * @param assembly - The tiles in the order shown and how many parts there are.
 * @returns What goes between the hidden fields and Check.
 */
export function fuxiAssembly(assembly: ShownAssembly): string {
  const sum = escapeHtml(bilingualText("Your chosen parts", "你选的部件"));
  const bank = escapeHtml(bilingualText("The parts", "部件"));
  const undo = bilingual("Undo", { simplified: "撤销", traditional: "撤銷" });
  const slots = Array.from({ length: assembly.places }, (_, place) =>
    slot(place),
  );

  return `<div class="cb-fuxi-zuzi" data-cb-fuxi-zuzi>
<p class="cb-fuxi-zuzi-sum" role="group" aria-label="${sum}" lang="${simplifiedLanguage}" data-cb-fuxi-zuzi-sum>${fuxiAssemblySum(slots)}</p>
<p class="visually-hidden" aria-live="polite" data-cb-fuxi-zuzi-said></p>
<div class="cb-fuxi-bank" role="group" aria-label="${bank}">
${assembly.tiles.map((part, place) => tile(part, place)).join("\n")}
</div>
<div class="cb-fuxi-pad-actions">
<button class="cb-fuxi-pad-action" type="button" data-cb-fuxi-zuzi-undo disabled>${undo}</button>
</div>
<input type="hidden" name="${typedField}" value="" data-cb-fuxi-arranged>
<noscript><p class="cb-fuxi-pad-note">${bilingual("Building a hanzi needs JavaScript. Skip this card, or turn hanzi building off in your 复习 settings.", { simplified: "拼字题需要 JavaScript。可以跳过这张卡片，或在复习设置里关掉拼字。", traditional: "拼字題需要 JavaScript。可以跳過這張卡片，或在複習設定裡關掉拼字。" })}</p></noscript>
</div>`;
}
